// Leg times (13 §13.1, 15 §15.1 "Leg breakdown"): where a shipment's door-to-door time went. The
// journey is cut at the moments a shipment reaches each step; the eight legs are the gaps between
// them. Pure: the server gathers the moments from box events, containers and milestones.
import { CLOCK_DEFAULTS } from './container-clocks';
import { msToDays } from './stats';

/**
 * The eight legs, in order.
 *   originDwell        collected -> loaded in a container (includes the trip to the export hub)
 *   consolidation      loaded -> sailed (loading, sealing, gate-in, waiting for the vessel)
 *   transit            sailed -> arrived at port
 *   portDwell          arrived -> customs lodged, plus customs released -> pulled out
 *   customs            customs lodged -> customs released
 *   devanning          pulled out -> box scanned in at the destination hub (haul and unloading)
 *   destinationDwell   scanned in at the hub -> handed to the partner or out for delivery
 *   delivery           handed over -> delivered (partner leg, or our own delivery)
 * A Prime parcel does not travel in a container: it has only `originDwell` (collected -> handed to
 * the courier) and `delivery`.
 */
export const LEG_KEYS = [
  'originDwell',
  'consolidation',
  'transit',
  'portDwell',
  'customs',
  'devanning',
  'destinationDwell',
  'delivery',
] as const;
export type LegKey = (typeof LEG_KEYS)[number];

export const LEG_LABELS: Readonly<Record<LegKey, string>> = {
  originDwell: 'Origin dwell',
  consolidation: 'Consolidation',
  transit: 'Transit',
  portDwell: 'Port dwell',
  customs: 'Customs',
  devanning: 'Devanning',
  destinationDwell: 'Destination dwell',
  delivery: 'Partner leg / own delivery',
};

export type LegDays = Readonly<Record<LegKey, number | null>>;
export type LegService = 'sea' | 'air' | 'prime';

/** The moments the journey is cut at. Each is the LAST box's: the customer waits for the last one. */
export interface LegPointsInput {
  readonly collectedAt: string;
  readonly loadedAt?: string | null | undefined;
  readonly departedAt?: string | null | undefined;
  readonly arrivedAt?: string | null | undefined;
  readonly customsLodgedAt?: string | null | undefined;
  readonly customsReleasedAt?: string | null | undefined;
  readonly pulledOutAt?: string | null | undefined;
  readonly atHubAt?: string | null | undefined;
  readonly handedOverAt?: string | null | undefined;
  readonly deliveredAt?: string | null | undefined;
}

export interface LegPointsOut {
  collectedAt: string;
  loadedAt: string | null;
  departedAt: string | null;
  arrivedAt: string | null;
  customsLodgedAt: string | null;
  customsReleasedAt: string | null;
  pulledOutAt: string | null;
  atHubAt: string | null;
  handedOverAt: string | null;
  deliveredAt: string | null;
}

export interface LegResult {
  /** The moments, in order: a moment that came before the one before it is moved up to it. */
  points: LegPointsOut;
  legs: LegDays;
  /** The legs that apply to the service. */
  applicable: LegKey[];
  /** Applicable legs that could not be worked out (an end of the leg is not known). */
  missing: LegKey[];
  /** Collected to delivered, in days; null until delivered. */
  doorToDoorDays: number | null;
  /** Delivered and every applicable leg known. */
  complete: boolean;
}

const POINT_ORDER = [
  'collectedAt',
  'loadedAt',
  'departedAt',
  'arrivedAt',
  'customsLodgedAt',
  'customsReleasedAt',
  'pulledOutAt',
  'atHubAt',
  'handedOverAt',
  'deliveredAt',
] as const;

const PRIME_LEGS: readonly LegKey[] = ['originDwell', 'delivery'];

function toMs(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Cuts a journey into legs. The moments are put in order first (a scan that arrives out of order
 * must not give a negative leg; a step dated after delivery is pulled back to the delivery), then
 * each leg is the gap between its two ends. A leg whose ends
 * are not both known is null and listed in `missing`; nothing is guessed.
 */
export function computeLegs(input: LegPointsInput, service: LegService = 'sea'): LegResult {
  const ms: Record<(typeof POINT_ORDER)[number], number | null> = {
    collectedAt: toMs(input.collectedAt),
    loadedAt: toMs(input.loadedAt),
    departedAt: toMs(input.departedAt),
    arrivedAt: toMs(input.arrivedAt),
    customsLodgedAt: toMs(input.customsLodgedAt),
    customsReleasedAt: toMs(input.customsReleasedAt),
    pulledOutAt: toMs(input.pulledOutAt),
    atHubAt: toMs(input.atHubAt),
    handedOverAt: toMs(input.handedOverAt),
    deliveredAt: toMs(input.deliveredAt),
  };
  // Collection and delivery are facts of the shipment; the steps between come from scans and from
  // dates staff enter on containers. A step dated after delivery is pulled back to it, so a wrong
  // container date can never lengthen door to door.
  const ceiling = ms.deliveredAt;
  let floor = Number.NEGATIVE_INFINITY;
  for (const key of POINT_ORDER) {
    const value = ms[key];
    if (value === null) continue;
    floor = Math.max(
      floor,
      ceiling !== null && key !== 'deliveredAt' ? Math.min(value, ceiling) : value,
    );
    ms[key] = floor;
  }

  const gap = (from: number | null, to: number | null): number | null =>
    from === null || to === null ? null : msToDays(to - from);
  const sum = (a: number | null, b: number | null): number | null =>
    a === null || b === null ? null : Math.round((a + b) * 100) / 100;

  const prime = service === 'prime';
  const hasCustoms = ms.customsLodgedAt !== null && ms.customsReleasedAt !== null;
  const noCustoms = ms.customsLodgedAt === null && ms.customsReleasedAt === null;
  let portDwell: number | null = null;
  if (hasCustoms) {
    portDwell = sum(
      gap(ms.arrivedAt, ms.customsLodgedAt),
      gap(ms.customsReleasedAt, ms.pulledOutAt),
    );
  } else if (noCustoms) {
    portDwell = gap(ms.arrivedAt, ms.pulledOutAt);
  }

  const legs: LegDays = {
    originDwell: gap(ms.collectedAt, prime ? ms.handedOverAt : ms.loadedAt),
    consolidation: prime ? null : gap(ms.loadedAt, ms.departedAt),
    transit: prime ? null : gap(ms.departedAt, ms.arrivedAt),
    portDwell: prime ? null : portDwell,
    customs: prime ? null : gap(ms.customsLodgedAt, ms.customsReleasedAt),
    devanning: prime ? null : gap(ms.pulledOutAt, ms.atHubAt),
    destinationDwell: prime ? null : gap(ms.atHubAt, ms.handedOverAt),
    delivery: gap(ms.handedOverAt, ms.deliveredAt),
  };

  const applicable = prime ? [...PRIME_LEGS] : [...LEG_KEYS];
  const missing = applicable.filter((key) => legs[key] === null);
  const iso = (v: number | null): string | null => (v === null ? null : new Date(v).toISOString());
  const doorToDoorDays = gap(ms.collectedAt, ms.deliveredAt);
  return {
    points: {
      collectedAt: iso(ms.collectedAt) ?? input.collectedAt,
      loadedAt: iso(ms.loadedAt),
      departedAt: iso(ms.departedAt),
      arrivedAt: iso(ms.arrivedAt),
      customsLodgedAt: iso(ms.customsLodgedAt),
      customsReleasedAt: iso(ms.customsReleasedAt),
      pulledOutAt: iso(ms.pulledOutAt),
      atHubAt: iso(ms.atHubAt),
      handedOverAt: iso(ms.handedOverAt),
      deliveredAt: iso(ms.deliveredAt),
    },
    legs,
    applicable,
    missing,
    doorToDoorDays,
    complete: doorToDoorDays !== null && missing.length === 0,
  };
}

// ------------------------------------------------------------------- routes and targets

/** `{origin sector}-{route code, else destination country}`, e.g. `KSA-MNL`. */
export function routeKey(
  originSectorId: string,
  destination: { routeCode?: string | undefined; country?: string | undefined },
): string {
  const to = destination.routeCode?.trim() || destination.country?.trim() || 'XX';
  return `${originSectorId}-${to}`;
}

/** The region a destination city reports under: its region, else its province, else "Unknown". */
export function regionOf(city: {
  region?: string | undefined;
  province?: string | undefined;
}): string {
  return city.region?.trim() || city.province?.trim() || 'Unknown';
}

export const DEFAULT_DOOR_TO_DOOR_DAYS = CLOCK_DEFAULTS.door_to_door.target.amount / 24;

export interface SlaPolicyFact {
  readonly clock: string;
  readonly scope?:
    | {
        readonly sectorId?: string | undefined;
        readonly partnerId?: string | undefined;
        readonly service?: string | undefined;
      }
    | undefined;
  readonly target: { readonly kind: string; readonly amount: number };
}

/** Days an SLA target stands for: hours over 24; working days as they are (as the website reads them). */
export function slaTargetDays(target: { kind: string; amount: number }): number {
  return target.kind === 'calendar_hours' ? target.amount / 24 : target.amount;
}

/**
 * The door-to-door target of a shipment: the most specific `door_to_door` policy (one for its
 * service, one for its origin or destination country, one for both), else the default (45 days).
 * Partner policies are for the partner leg and are skipped. Two equally specific policies: the
 * stricter one, so a tie never flatters the numbers.
 */
export function doorToDoorTarget(
  policies: readonly SlaPolicyFact[],
  shipment: {
    originSectorId: string;
    destinationSectorId?: string | null | undefined;
    service: LegService;
  },
): { days: number; source: 'sla' | 'default' } {
  let best: { score: number; days: number } | undefined;
  for (const policy of policies) {
    if (policy.clock !== 'door_to_door') continue;
    const scope = policy.scope ?? {};
    if (scope.partnerId !== undefined) continue;
    if (
      scope.sectorId !== undefined &&
      scope.sectorId !== shipment.originSectorId &&
      scope.sectorId !== shipment.destinationSectorId
    ) {
      continue;
    }
    if (scope.service !== undefined && scope.service !== shipment.service) continue;
    const score = (scope.sectorId === undefined ? 0 : 1) + (scope.service === undefined ? 0 : 1);
    const days = slaTargetDays(policy.target);
    if (!best || score > best.score || (score === best.score && days < best.days)) {
      best = { score, days };
    }
  }
  return best
    ? { days: best.days, source: 'sla' }
    : { days: DEFAULT_DOOR_TO_DOOR_DAYS, source: 'default' };
}

/** Within target: the days taken against the days allowed. Null when either is unknown. */
export function withinTarget(
  days: number | null | undefined,
  target: number | null | undefined,
): boolean | null {
  return days === null || days === undefined || target === null || target === undefined
    ? null
    : days <= target;
}
